import { FormEditTabType, FormSettingsTabType, getWebPath } from '@doss/common-all'
import { MoreVertRounded } from '@mui/icons-material'
import { useState } from 'react'
import { RxFilePlus, RxTrash } from 'react-icons/rx'
import { useNavigate } from 'react-router'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '~/@/components/ui/dropdown-menu'
import Typography from '~/@/components/ui/typography'
import {
  BreadcrumbObjectType,
  GetAllFormsV2Document,
  useDuplicateFormMutation,
  useDuplicateFormV2Mutation,
} from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import config from '../../common/config/config'
import { useGlobalContext } from '../../common/hooks/useGlobalContext'
import { useDossSnackbar } from '../shared/DossSnackbar'
import { SendEmailDialog } from '../shared/SendEmailDialog'
import { useFormTabSettings } from './form-settings-tab/FormSettingsTabProvider'

/**
 * Renders the dropdown action menu for duplicating or deleting a form
 */
export function ActionMenu({ formId }: { formId: string }) {
  const { user } = useGlobalContext()
  const navigate = useNavigate()
  const [duplicateForm] = useDuplicateFormMutation()
  const [duplicateFormV2] = useDuplicateFormV2Mutation({
    refetchQueries: [{ query: GetAllFormsV2Document }],
  })
  const { t } = useTypedTranslation()
  const snackbar = useDossSnackbar()
  const [openEmailDialog, setOpenEmailDialog] = useState(false)
  const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState(false)
  const emailBodyAdmin = `Admin details:\n
  - Requested by: ${user?.firstName} ${user?.lastName} (${user?.email})
  - Organization ID: ${user?.organizationId}
  - URL: ${window.location.href}`

  const { isFormV2 } = useFormTabSettings()

  const handleDuplicateFormClick = async () => {
    if (isFormV2) {
      await duplicateFormV2({ variables: { formV2Id: formId } })
        .then((result) => {
          if (!result.data) {
            snackbar.showError(t('form.duplicate.error'))
            return
          }
          snackbar.showSuccess(t('form.duplicate.success'))
          navigate(
            getWebPath({
              objectType: BreadcrumbObjectType.FORM,
              objectId: result.data.duplicateFormV2.id,
              settings: true,
              settingsParams: {
                tab: FormEditTabType.SETTINGS,
                settingsTab: FormSettingsTabType.DETAILS,
              },
            }),
          )
        })
        .catch(() => {
          snackbar.showError(t('form.duplicate.error'))
        })
      return
    }

    await duplicateForm({ variables: { formId } })
      .then((result) => {
        if (!result.data) {
          snackbar.showError(t('form.duplicate.error'))
          return
        }
        snackbar.showSuccess(t('form.duplicate.success'))
        // Route to the new form's setting page
        navigate(
          getWebPath({
            objectType: BreadcrumbObjectType.FORM,
            objectId: result.data.duplicateForm.id,
            settings: true,
            settingsParams: {
              tab: FormEditTabType.SETTINGS,
              settingsTab: FormSettingsTabType.DETAILS,
            },
          }),
        )
      })
      .catch(() => {
        snackbar.showError(t('form.duplicate.error'))
        return
      })
  }

  return (
    <>
      <DropdownMenu
        onOpenChange={(open) => {
          setIsDropdownMenuOpen(open)
        }}
        open={isDropdownMenuOpen}
      >
        <DropdownMenuTrigger onClick={() => setIsDropdownMenuOpen(!isDropdownMenuOpen)}>
          <button className="flex cursor-pointer items-center justify-center rounded p-2 hover:bg-gray-1">
            <MoreVertRounded fontSize="small" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuPortal>
          <DropdownMenuContent align="end" className="z-[1202] bg-white p-0" sideOffset={5}>
            <DropdownMenuItem
              className="cursor-pointer"
              onSelect={() => {
                handleDuplicateFormClick()
                setIsDropdownMenuOpen(false)
              }}
            >
              <div className="flex max-w-[150px] items-center gap-2 p-1.5">
                <RxFilePlus className="shrink-0" />
                <Typography variant="body3">{t('form.duplicate.title')}</Typography>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer"
              onSelect={() => {
                setOpenEmailDialog(true)
                setIsDropdownMenuOpen(false)
              }}
            >
              <div className="flex max-w-[150px] items-center gap-2 p-1.5">
                <RxTrash className="shrink-0" />
                <Typography variant="body3">{t('form.duplicate.delete_form')}</Typography>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>
      <SendEmailDialog
        defaultBody={'Hi team, \n\n[Please provide a reason for deleting the form]'}
        defaultSubject={`[${user?.organizationName}] Delete Form Request`}
        defaultTo={config.supportEmail}
        emailBodyAdmin={emailBodyAdmin}
        onClose={() => setOpenEmailDialog(false)}
        open={openEmailDialog}
        title={'Delete Form Request'}
      />
    </>
  )
}
